import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import FAQSection from './components/FAQSection';
import Footer from '../home/components/Footer';

const faqs = [
  {
    question: 'Do modular pods need council approval?',
    answer: 'It depends. Approval requirements depend on the council, the pod\'s intended use, its size, and where on your property it will be placed. Some small pods used as home offices or backyard studios may fall under exempt or complying development in certain states, but this varies widely. You should always confirm with your local council or a qualified planning professional before ordering a pod. Nexa Pods can provide specification documents to assist with your application, but we do not guarantee approval outcomes.',
  },
  {
    question: 'Can I put a pod in my backyard?',
    answer: 'It depends on your local council regulations, property zoning, and how you plan to use the pod. Some councils allow backyard pods as secondary dwellings or home offices, while others have stricter rules around size, setbacks, and utility connections. Site access is another factor — the pod needs a clear path from the street to the installation location. We recommend discussing your specific property with a local planning consultant or your council early in the process.',
  },
  {
    question: 'How much does a modular cabin cost?',
    answer: 'Prices on our website reflect starting model costs and do not include transport, site preparation, installation, council fees, or utility connections. Total project costs vary depending on the model you choose, your location, site conditions, access requirements, and any customisation. For a realistic project estimate tailored to your site, we recommend requesting a site and model assessment through our contact page.',
  },
  {
    question: 'What site access is needed?',
    answer: 'Site access requirements depend on the pod model and your property. Generally, you will need a clear, level path wide enough for delivery vehicles and lifting equipment. Factors such as overhead obstructions, steep driveways, soft ground, and proximity to neighbouring properties can all affect access. A site assessment helps identify any constraints early so you can plan accordingly.',
  },
  {
    question: 'Are utilities included?',
    answer: 'Pods can include pre-wired electrical systems, plumbing rough-ins, and fixtures depending on the model and specification level. However, connection to mains power, water, and sewer or septic systems is typically managed separately on site and requires licensed trades. Some models support off-grid configurations with solar power and water storage — these options can be discussed during the planning phase.',
  },
  {
    question: 'How long does delivery and installation take?',
    answer: 'Lead times and installation duration vary by model, order volume, destination, and site readiness. Transport logistics, weather conditions, and council approval timelines also influence the schedule. Once your order is confirmed and site preparation is complete, we provide an estimated timeframe. We recommend allowing adequate buffer in your project timeline to account for variables outside anyone\'s control.',
  },
  {
    question: 'Can pods be used for glamping or resorts?',
    answer: 'Yes. Many of our pod models are well suited to hospitality applications including glamping sites, eco-resorts, boutique accommodation, and tourism ventures. Different models suit different settings — for example, compact capsules work well for individual guest stays, while larger units can serve as reception areas or communal spaces. Site planning, council approval for commercial use, and utility infrastructure are important considerations for hospitality projects.',
  },
  {
    question: 'How do I choose a model?',
    answer: 'Start with your intended use: is it a home office, guest accommodation, glamping pod, or something else? Consider the number of occupants, desired features, site constraints, and budget range. Browse our product range to compare models, then get in touch for a personalised recommendation. Our team can help you narrow down the options based on your specific site, goals, and requirements.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq, index) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
    position: index + 1,
  })),
};

const modelComparison = [
  {
    series: 'A Series',
    tagline: 'Workplace Solution',
    description: 'Smart modular office spaces designed for productivity, work, and commercial applications. Suitable for home offices, site offices, and commercial pods.',
    models: 'A1, A2, A3',
    link: '/a-range-details',
    icon: 'ri-briefcase-line',
  },
  {
    series: 'X Series',
    tagline: 'Luxury Living',
    description: 'Premium space capsules combining futuristic design with durability. Models X3, X5, X6, X7, Y6, and V6 offer a range of sizes and layouts for private retreats, guest accommodation, and hospitality.',
    models: 'X3, X5, X6, X7, Y6, V6',
    link: '/x-range-details',
    icon: 'ri-home-6-line',
  },
  {
    series: 'M Series',
    tagline: 'Modular Adventure',
    description: 'Flat-pack modular cabins engineered for remote and challenging locations. Models M5, M7, and M9 suit eco-tourism, off-grid retreats, and sites with limited access.',
    models: 'M5, M7, M9',
    link: '/m-range-details',
    icon: 'ri-compass-3-line',
  },
  {
    series: 'Z Series',
    tagline: 'Future Living — Coming Soon',
    description: 'Space capsule-style living units for luxury accommodation and immersive stay experiences. Designed for the premium end of glamping, resorts, and unique short-stay accommodation.',
    models: 'Coming Soon',
    link: '/z-range-details',
    icon: 'ri-rocket-line',
  },
];

export default function BuyerGuidePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="Modular Pod Buyer's Guide Australia | Nexa Pods"
        description="Read the Nexa Pods buyer's guide to modular pods in Australia, covering pricing, planning, site access, approvals, delivery and model selection."
        canonicalPath="/modular-pod-buyers-guide"
        keywords="modular pod buyers guide Australia, prefab cabin guide, capsule house buyer guide, Nexa Pods guide, modular pod planning Australia"
        jsonLd={faqJsonLd}
      />
      <div className="bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000] text-white min-h-screen w-full overflow-x-hidden">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
          scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-500/20' : 'bg-transparent'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between w-full">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <img 
                src="/images/c719b13a28cc.webp" 
                alt="Space Capsule Living" 
                className="h-16 sm:h-20 lg:h-28 w-auto"
              />
            </Link>
            
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>

            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              
              <div 
                className="relative"
                onMouseEnter={() => setProductsDropdownOpen(true)}
                onMouseLeave={() => setProductsDropdownOpen(false)}
              >
                <Link 
                  to="/product-range"
                  className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </Link>
                
                {productsDropdownOpen && (
                  <div className="absolute top-full left-0 pt-2 w-auto">
                    <div className="bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.3)] overflow-hidden">
                      <Link to="/product-range" className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap">All Products</Link>
                      <Link to="/a-range-details" className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap">A Series - Workplace Solution</Link>
                      <Link to="/x-range-details" className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap">X Series - Luxury Living</Link>
                      <Link to="/m-range-details" className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap">M Series - Modularize</Link>
                      <Link to="/z-range-details" className="block px-6 py-3 text-base text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap">Z Series - Coming Soon</Link>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/modular-pod-buyers-guide" className="text-base text-cyan-400 hover:text-cyan-300 transition-colors whitespace-nowrap cursor-pointer">Buyer Guide</Link>
              <Link to="/contact" className="px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 w-full">
              <div className="px-4 py-6 space-y-4 w-full">
                <Link to="/" onClick={closeMobileMenu} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Home</Link>
                
                <div>
                  <button 
                    onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                    className="w-full flex items-center justify-between text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer"
                  >
                    Products
                    <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  
                  {productsDropdownOpen && (
                    <div className="mt-2 ml-4 space-y-2 bg-black/90 border border-cyan-500/30 rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.2)] overflow-hidden">
                      <Link to="/product-range" onClick={closeMobileMenu} className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap">All Products</Link>
                      <Link to="/a-range-details" onClick={closeMobileMenu} className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap">A Series - Workplace Solution</Link>
                      <Link to="/x-range-details" onClick={closeMobileMenu} className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap">X Series - Luxury Living</Link>
                      <Link to="/m-range-details" onClick={closeMobileMenu} className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap">M Series - Modularize</Link>
                      <Link to="/z-range-details" onClick={closeMobileMenu} className="block px-4 py-2 text-sm text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap">Z Series - Coming Soon</Link>
                    </div>
                  )}
                </div>

                <Link to="/modular-pod-buyers-guide" onClick={closeMobileMenu} className="block text-base text-cyan-400 hover:text-cyan-300 transition-colors py-2 cursor-pointer">Buyer Guide</Link>
                <Link to="/contact" onClick={closeMobileMenu} className="block w-full text-center px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer">
                  Contact Us
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Hero */}
        <section className="relative pt-40 pb-24 px-4 md:px-8">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse" style={{ top: '25%', left: '15%', animationDelay: '0s' }}></div>
            <div className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse" style={{ top: '55%', left: '75%', animationDelay: '1s' }}></div>
            <div className="absolute w-1 h-1 bg-blue-400 rounded-full animate-pulse" style={{ top: '35%', left: '45%', animationDelay: '2s' }}></div>
          </div>

          <div className="max-w-7xl mx-auto text-center relative z-10">
            <p className="text-gray-500 text-sm uppercase tracking-[0.25em] mb-6">Buyer's Guide</p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-thin tracking-wide leading-tight mb-6">
              Modular Pod<br />
              <span className="text-cyan-400" style={{ textShadow: '0 0 30px rgba(0,217,255,0.6)' }}>Buyer's Guide</span> for Australia
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
              Everything you need to know before choosing a modular pod, prefab cabin, or capsule house — from planning and approvals through to delivery and installation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link to="/contact" className="px-8 py-3.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
                Request a Site &amp; Model Assessment
              </Link>
              <Link to="/product-range" className="px-8 py-3.5 text-gray-300 hover:text-cyan-400 transition-colors underline decoration-cyan-500/50 hover:decoration-cyan-400 whitespace-nowrap cursor-pointer">
                Browse Product Range
              </Link>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-20 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-thin mb-8">
                What Is a <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>Modular Pod</span>?
              </h2>
              <div className="space-y-5 text-gray-300 text-lg leading-relaxed">
                <p>
                  A modular pod is a prefabricated, self-contained structure built off-site and delivered to your property. Also referred to as prefab cabins, capsule houses, or modular dwellings, these units offer a flexible alternative to traditional construction for a wide range of uses.
                </p>
                <p>
                  Across Australia, modular pods are increasingly being used as <strong className="text-white">backyard studios and home offices</strong>, <strong className="text-white">private retreats and guest accommodation</strong>, <strong className="text-white">glamping sites and eco-tourism stays</strong>, <strong className="text-white">resort and hospitality suites</strong>, and <strong className="text-white">flexible commercial spaces</strong>. Each application brings its own set of considerations around size, features, approvals, and site requirements.
                </p>
                <p>
                  Nexa Pods supplies a curated range of modular pod models across four product series — from compact workplace pods to luxury living capsules and flat-pack adventure cabins. This guide walks you through the key things to consider before making a decision.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Before You Choose a Pod */}
        <section className="py-20 px-4 md:px-8 bg-[#0a0a0f]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-thin mb-4">
                Before You <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>Choose a Pod</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">Six key factors to consider before you start comparing models</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-focus-3-line', title: 'Intended Use', desc: 'Is it a home office, guest accommodation, glamping pod, or commercial space? Your use determines size, layout, and feature requirements — and can affect approval pathways.' },
                { icon: 'ri-map-pin-line', title: 'Location & Zoning', desc: 'Your property\'s zoning and council regulations shape what is permitted. Rural, residential, and commercial zones each have different rules that affect pod placement and use.' },
                { icon: 'ri-truck-line', title: 'Site Access', desc: 'Can delivery vehicles and lifting equipment reach the installation spot? Consider driveway width, overhead clearance, gate dimensions, and ground conditions along the delivery path.' },
                { icon: 'ri-plug-line', title: 'Utilities', desc: 'How will the pod connect to power, water, and drainage? Some sites suit mains connections; others may need off-grid solutions. Utility planning should happen early in the process.' },
                { icon: 'ri-landscape-line', title: 'Site Conditions', desc: 'Slope, soil type, drainage, and vegetation all affect site preparation. A level, stable foundation area typically reduces preparation work and cost.' },
                { icon: 'ri-money-dollar-circle-line', title: 'Budget', desc: 'Beyond the pod itself, budget for transport, site preparation, foundation work, utility connections, council fees, and any professional services you may need.' },
              ].map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/20 rounded-xl p-6 hover:border-cyan-500/40 transition-all">
                  <div className="w-12 h-12 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-3xl text-cyan-400`}></i>
                  </div>
                  <h3 className="text-white font-medium text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Approvals in Victoria and Australia */}
        <section className="py-20 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-thin mb-8">
                Approvals in Victoria <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>&amp; Australia</span>
              </h2>
              <div className="space-y-5 text-gray-300 text-lg leading-relaxed">
                <p>
                  Council approval requirements for modular pods vary by <strong className="text-white">state, local council, property zoning, pod size, and intended use</strong>. A pod used as a home office in one council area may follow a different process than the same pod used as guest accommodation in another.
                </p>
                <p>
                  In Victoria, some small structures may fall under specific planning exemptions, but this is not universal. In other states — New South Wales, Queensland, South Australia, Western Australia, Tasmania, the ACT, and the Northern Territory — rules differ and are subject to change.
                </p>
                <p>
                  Nexa Pods does not make representations about universal approval or compliance. <strong className="text-white">You should confirm requirements with your local council and, where appropriate, engage a qualified planning professional or building surveyor</strong> before committing to a purchase. We can provide specification documents to support your application, but the approval process is managed by you and your advisors.
                </p>
              </div>
              <div className="mt-10 p-6 bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/20 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
                    <i className="ri-information-line text-2xl text-cyan-400"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Key takeaway</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">Approval requirements are specific to your site and use case. Start the conversation with your council early, and factor approval timelines into your overall project schedule.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Site Preparation, Transport & Installation */}
        <section className="py-20 px-4 md:px-8 bg-[#0a0a0f]">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-thin mb-8">
                Site Preparation, Transport <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>&amp; Installation</span>
              </h2>
              <div className="space-y-5 text-gray-300 text-lg leading-relaxed">
                <p>
                  Once you have selected a model and confirmed approvals, the next stage is preparing your site and coordinating delivery. The process typically involves three phases:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
                  {[
                    { step: '01', title: 'Site Preparation', desc: 'Levelling, foundation work, access path clearing, and utility rough-ins. Requirements vary significantly based on site conditions, pod model, and intended use.' },
                    { step: '02', title: 'Transport & Delivery', desc: 'Pods are transported via truck or container. Timing depends on distance, route conditions, and logistics scheduling. Delivery is arranged once the site is ready.' },
                    { step: '03', title: 'Installation & Connection', desc: 'Crane or lifting equipment positions the pod on its prepared foundation. Utility connections and finishing work are completed by licensed trades on site.' },
                  ].map((item, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/20 rounded-xl p-6">
                      <div className="text-4xl font-bold text-cyan-400/30 mb-3">{item.step}</div>
                      <h3 className="text-white font-medium mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-8">
                  <strong className="text-white">Timing varies.</strong> Lead times, transport duration, and installation schedules depend on model availability, destination, site readiness, weather, and contractor availability. We provide estimated timeframes after reviewing your specific project — these are indicative and not guaranteed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Price Explanation */}
        <section className="py-20 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-thin mb-8">
                Understanding <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>Pricing</span>
              </h2>
              <div className="space-y-5 text-gray-300 text-lg leading-relaxed">
                <p>
                  The prices displayed on our product pages reflect <strong className="text-white">starting model costs only</strong>. Your total project investment will depend on several factors beyond the pod itself:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  {[
                    'Transport to your site',
                    'Site preparation and foundation work',
                    'Crane or lifting equipment hire',
                    'Utility connections (power, water, drainage)',
                    'Council application fees and professional services',
                    'Any customisation or specification upgrades',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-cyan-400 text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-base">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6">
                  For a realistic, site-specific estimate, we recommend <Link to="/contact" className="text-cyan-400 hover:text-cyan-300 underline cursor-pointer">requesting a project assessment</Link>. This gives us an opportunity to understand your property and goals before providing indicative cost ranges.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Model Comparison */}
        <section className="py-20 px-4 md:px-8 bg-[#0a0a0f]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-thin mb-4">
                Compare <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>Models</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">Four product series designed for different uses and site conditions</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {modelComparison.map((series, index) => (
                <div key={index} className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/20 rounded-xl p-6 flex flex-col hover:border-cyan-500/40 transition-all">
                  <div className="w-12 h-12 flex items-center justify-center mb-4">
                    <i className={`${series.icon} text-3xl text-cyan-400`}></i>
                  </div>
                  <h3 className="text-white font-medium text-lg mb-1">{series.series}</h3>
                  <p className="text-cyan-400 text-sm mb-3">{series.tagline}</p>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4 flex-1">{series.description}</p>
                  <p className="text-gray-500 text-xs mb-4">Models: {series.models}</p>
                  <Link 
                    to={series.link}
                    className="mt-auto px-4 py-2.5 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium hover:bg-cyan-500/10 hover:border-cyan-400/60 transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    View {series.series}
                  </Link>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/product-range" className="inline-flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer text-sm">
                View full product range
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-5xl font-thin mb-4">
                  Frequently Asked <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>Questions</span>
                </h2>
              </div>
              <FAQSection faqs={faqs} />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 md:px-8 bg-[#0a0a0f]">
          <div className="max-w-7xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-[#14141e]/80 to-[#0a0a0f]/80 backdrop-blur-xl">
              <div className="absolute inset-0 opacity-5" style={{
                backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(0,217,255,0.5) 0%, transparent 60%), radial-gradient(circle at 70% 50%, rgba(0,217,255,0.3) 0%, transparent 60%)',
              }}></div>
              <div className="relative z-10 p-10 md:p-16 text-center">
                <h2 className="text-3xl md:text-4xl font-thin mb-4">
                  Ready to <span className="text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.4)' }}>Get Started</span>?
                </h2>
                <p className="text-gray-400 text-lg max-w-xl mx-auto mb-8">
                  Tell us about your site and what you're planning. We'll help you narrow down the options and provide realistic, site-specific guidance.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/contact" className="px-8 py-3.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
                    Request a Site &amp; Model Assessment
                  </Link>
                  <Link to="/product-specifications" className="px-8 py-3.5 text-gray-300 hover:text-cyan-400 transition-colors underline decoration-cyan-500/50 hover:decoration-cyan-400 whitespace-nowrap cursor-pointer">
                    View Specifications
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
